import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/services/api.service';
import { HttpHeaders } from '@angular/common/http';
import { ITearSheet, ITearsheetCommentary } from '@common/models/ITearSheet';
import { Observable } from 'rxjs';
import { IApiStatus } from '@common/models/IApiStatus';
import { IPortfolioCompanyMetric } from '@common/models/IPortfolioCompanyMetric';

@Injectable({
    providedIn: 'root'
})
export class TearsheetService {
    constructor(private apiService: ApiService) {}

    public updateCommentary(id: string, data: ITearsheetCommentary): Observable<void> {
        return this.apiService.put(`PortfolioCompany/${id}/Tearsheet/commentary`, data);
    }

    public updateTearsheet(id: string, data: ITearSheet): Observable<IApiStatus>{
        const updateData = structuredClone(data);
        // Delete tag
        if (updateData?.investmentSummaryWithExcludePartner) {
            delete updateData?.investmentSummaryWithExcludePartner;
        }
        return this.apiService.put(`PortfolioCompany/${id}/Tearsheet`, updateData);
    }

    public getMetrics(id: string, quarter: string): Observable<IPortfolioCompanyMetric[]> {
        const url = `PortfolioCompany/${id}/Metric/${quarter}`;
        return this.apiService.get(url);
    }

    public updatePcMetricCode(id: string, data: IPortfolioCompanyMetric[]): Observable<any> {
        const url = `PortfolioCompany/${id}/Metric/bulk`;
        return this.apiService.post(url, data);
    }

    public downloadTearsheet(id: string, fund: string, quarter: string): Observable<any> {
        const url = `PortfolioCompany/${id}/Tearsheet/download/${fund}/${quarter}`;
        return this.apiService.post(url, {}, {
            headers: new HttpHeaders().set('Accept', "application/pdf"),
            responseType: 'blob' as 'json',
            observe: 'response',
        });
    }

    public downloadAggregatedTearsheet(id: string, fund: string, quarter: string): Observable<any> {
        const url = `PortfolioCompany/${id}/Tearsheet/download/aggregated/${fund}/${quarter}`;
        return this.apiService.post(url, {}, {
            headers: new HttpHeaders().set('Accept', "application/pdf"),
            responseType: 'blob' as 'json',
            observe: 'response',
        });
    }

    public downloadCombinedTearsheet(id: string, fund: string, quarter: string): Observable<any> {
        const url = `PortfolioCompany/${id}/Tearsheet/${fund}/${quarter}/combinedTearsheet`;
        return this.apiService.post(url, {}, {
            headers: new HttpHeaders().set('Accept', "application/pdf"),
            responseType: 'blob' as 'json',
            observe: 'response',
        });
    }

    public downloadTearsheetHtml(id: string, fund: string, quarter: string): Observable<any> {
        const url = `PortfolioCompany/${id}/Tearsheet/download/html/${fund}/${quarter}`;
        return this.apiService.getV2(url, {
            headers: new HttpHeaders().set('Accept', "text/html"),
            responseType: 'blob' as 'json',
        });
    }

    public getTearsheetByName(mnemonic: string, name: string){
        const url = `PortfolioCompany/${mnemonic}/Tearsheet/${name}`;
        return this.apiService.get(url);
    }

    public getLatestTearsheetVersion(url: string): Observable<any>{
        return this.apiService.get(url);
    }
    public bulkUpsert(requestBody: ITearSheet[]): Observable<IApiStatus> {
        return this.apiService.post(`PortfolioCompany/All/Tearsheet/bulk`, requestBody)
    }
    public downloadLiveTearsheet(mnemonic: string, fund: string, data: ITearSheet): Observable<ArrayBuffer> {
        const updateData = structuredClone(data);
        // Delete tag
        if (updateData?.investmentSummaryWithExcludePartner) {
            delete updateData?.investmentSummaryWithExcludePartner;
        }
        return this.apiService.post(`PortfolioCompany/${mnemonic}/Tearsheet/download-live/${encodeURIComponent(fund)}`,
            updateData,
            { responseType: 'arraybuffer' }
        );
    }

    public getCombinedPCList(mnemonic: string): Observable<any[]> {
        const url = `PortfolioCompany/${mnemonic}/Tearsheet/combined/pc-list`;
        return this.apiService.get(url);
    }

}
